import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Edit, Save, Loader2 } from "lucide-react";
import { apiGet, apiPost, apiPut } from "@/lib/api";

const TIP_CATEGORII = [
  { cod: "toc_oblon", nume: "Toc Oblon (rama exterioara)" },
  { cod: "cercevea_oblon", nume: "Cercevea Oblon (rama canat)" },
  { cod: "traversa_oblon", nume: "Traversa Oblon (impartitor orizontal)" },
  { cod: "montant_oblon", nume: "Montant Oblon (impartitor vertical)" },
];

const emptyItem = {
  cod: "", nume: "", descriere: "",
  serie: "Oblon-STD", subserie: "Standard",
  tip_categorie: "toc_oblon",
  poate_imparti: false, poate_deschidere: false,
  poate_imparti_in: [], compatibil_cu: [],
  ordine: 0, activ: true,
};

export default function ProfileObloaneSection({ onStatsChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterCat, setFilterCat] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet(`/obloane-config/profile${filterCat ? `?tip_categorie=${filterCat}` : ""}`);
      setItems(data.items || []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [filterCat]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openModal = (item = null) => {
    setEditing(item ? { ...item, _isEdit: true } : { ...emptyItem });
    setShowModal(true);
  };

  const toggleArr = (field, value) => {
    setEditing((prev) => {
      const arr = prev[field] || [];
      return { ...prev, [field]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value] };
    });
  };

  const saveItem = async () => {
    if (!editing.cod || !editing.nume) {
      toast.error("Cod si nume sunt obligatorii");
      return;
    }
    const isEdit = !!editing._isEdit;
    setSaving(true);
    try {
      if (isEdit) {
        await apiPut(`/obloane-config/profile/${editing.cod}`, editing);
      } else {
        await apiPost("/obloane-config/profile", editing);
      }
      toast.success(isEdit ? "Actualizat" : "Adaugat");
      setShowModal(false);
      fetchData();
      onStatsChange?.();
    } catch (err) {
      toast.error(err.message || "Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="py-16 flex justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="oblon-profile-section">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <h2 className="text-lg font-semibold text-slate-800">Profile Oblon (Categorii)</h2>
        <div className="flex gap-2">
          <Select value={filterCat || "all"} onValueChange={(v) => setFilterCat(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[220px]" data-testid="filter-categorie">
              <SelectValue placeholder="Toate categoriile" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toate categoriile</SelectItem>
              {TIP_CATEGORII.map((c) => (
                <SelectItem key={c.cod} value={c.cod}>{c.nume}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={() => openModal()} data-testid="add-profile-btn">
            <Plus className="h-4 w-4 mr-1" /> Adauga
          </Button>
        </div>
      </div>

      <p className="text-sm text-slate-500">
        Defineste profile oblon (toc, cercevea, traversa, montant) si compatibilitatile dintre ele.
      </p>

      <div className="grid gap-3">
        {items.length === 0 && (
          <Card className="p-6 text-center text-slate-400">Niciun profil. Apasa Adauga pentru a defini primul.</Card>
        )}
        {items.map((item) => (
          <Card key={item.cod} className={`p-3 ${item.activ === false ? "opacity-60 bg-slate-50" : ""}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs bg-slate-200 px-2 py-0.5 rounded">#{item.ordine ?? "-"}</span>
                  <span className="font-mono text-sm bg-slate-100 px-2 py-0.5 rounded">{item.cod}</span>
                  <span className="font-medium">{item.nume}</span>
                  {item.poate_imparti && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Impartire</span>}
                  {item.poate_deschidere && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Deschidere</span>}
                </div>
                {item.descriere && <p className="text-sm text-slate-500 mt-1">{item.descriere}</p>}
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-slate-400">
                  <span>Serie: {item.serie || "-"}</span>
                  <span>Subserie: {item.subserie || "-"}</span>
                  <span>Categorie: {item.tip_categorie}</span>
                </div>
                {item.compatibil_cu?.length > 0 && (
                  <p className="text-xs text-purple-600 mt-1">Compatibil cu: {item.compatibil_cu.join(", ")}</p>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={() => openModal(item)} data-testid={`edit-profile-${item.cod}`}>
                <Edit className="h-4 w-4" />
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?._isEdit ? "Editare" : "Adaugare"} Profil Oblon</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pb-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Cod *</Label>
                <Input
                  data-testid="profile-cod-input"
                  value={editing?.cod || ""}
                  onChange={(e) => setEditing({ ...editing, cod: e.target.value.toLowerCase().replace(/\s/g, "_") })}
                  disabled={editing?._isEdit}
                />
              </div>
              <div>
                <Label>Nume *</Label>
                <Input
                  data-testid="profile-nume-input"
                  value={editing?.nume || ""}
                  onChange={(e) => setEditing({ ...editing, nume: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Descriere</Label>
              <Input value={editing?.descriere || ""} onChange={(e) => setEditing({ ...editing, descriere: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Serie</Label>
                <Input value={editing?.serie || ""} onChange={(e) => setEditing({ ...editing, serie: e.target.value })} />
              </div>
              <div>
                <Label>Subserie</Label>
                <Input value={editing?.subserie || ""} onChange={(e) => setEditing({ ...editing, subserie: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Categorie</Label>
              <Select value={editing?.tip_categorie || "toc_oblon"} onValueChange={(v) => setEditing({ ...editing, tip_categorie: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIP_CATEGORII.map((c) => (
                    <SelectItem key={c.cod} value={c.cod}>{c.nume}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3 p-3 border rounded-lg bg-blue-50">
              <input
                type="checkbox" id="poate_deschidere"
                checked={!!editing?.poate_deschidere}
                onChange={(e) => setEditing({ ...editing, poate_deschidere: e.target.checked })}
                className="h-5 w-5"
              />
              <label htmlFor="poate_deschidere" className="cursor-pointer">
                <div className="font-medium">Poate fi folosit pentru DESCHIDERE</div>
                <p className="text-xs text-slate-500">Cercevea/canat mobil</p>
              </label>
            </div>
            <div className="flex items-center gap-3 p-3 border rounded-lg bg-green-50">
              <input
                type="checkbox" id="poate_imparti"
                checked={!!editing?.poate_imparti}
                onChange={(e) => setEditing({ ...editing, poate_imparti: e.target.checked })}
                className="h-5 w-5"
              />
              <label htmlFor="poate_imparti" className="cursor-pointer">
                <div className="font-medium">Poate fi folosit pentru IMPARTIRE</div>
                <p className="text-xs text-slate-500">Montant / traversa</p>
              </label>
            </div>
            <div className="p-3 border rounded-lg bg-purple-50">
              <Label className="font-medium">Compatibil cu:</Label>
              <p className="text-xs text-slate-500 mb-2">Profile cu care se poate cupla</p>
              <div className="flex flex-wrap gap-2">
                {items.filter((i) => i.cod !== editing?.cod).map((p) => (
                  <label key={p.cod} className="flex items-center gap-1.5 px-2 py-1 bg-white rounded border cursor-pointer">
                    <input
                      type="checkbox"
                      checked={(editing?.compatibil_cu || []).includes(p.cod)}
                      onChange={() => toggleArr("compatibil_cu", p.cod)}
                      className="h-4 w-4"
                    />
                    <span className="text-sm">{p.nume}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Ordine</Label>
                <Input type="number" value={editing?.ordine || 0} onChange={(e) => setEditing({ ...editing, ordine: parseInt(e.target.value, 10) || 0 })} />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox" checked={editing?.activ !== false}
                    onChange={(e) => setEditing({ ...editing, activ: e.target.checked })}
                    className="h-4 w-4"
                  />
                  <span className="text-sm">Activ</span>
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Anuleaza</Button>
            <Button onClick={saveItem} disabled={saving} data-testid="save-profile-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Salveaza
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
